// src/utils/exportReport.ts
/**
 * Shared export utility — generates xlsx, csv, or pdf from a report's rows + columns.
 * One function used by every page that has an Export icon (Chart of accounts,
 * Bank transactions, Account List report, future reports).
 */

import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { buildReportFileName } from './printReport';

export type ExportFormat = 'xlsx' | 'csv' | 'pdf';

export interface ExportColumn {
  key: string;
  label: string;
  /** Optional width in points (pdf) or chars (xlsx). */
  width?: number;
  /** 'left' | 'center' | 'right'. Default: left. */
  align?: 'left' | 'center' | 'right';
}

export interface ExportOptions {
  reportName: string;
  columns: ExportColumn[];
  rows: Array<Record<string, string | number | null | undefined>>;
  /** Optional company name shown as the first header row. */
  companyName?: string;
  /** Optional subtitle shown under the company name. */
  subtitle?: string;
}

export function exportReport(format: ExportFormat, options: ExportOptions): void {
  const fileName = buildReportFileName(options.reportName);

  if (format === 'csv') {
    exportCsv(fileName, options);
    return;
  }

  if (format === 'xlsx') {
    exportXlsx(fileName, options);
    return;
  }

  if (format === 'pdf') {
    exportPdf(fileName, options);
    return;
  }
}

function exportCsv(fileName: string, opts: ExportOptions): void {
  const headers = opts.columns.map(function (c) { return escapeCsv(c.label); }).join(',');
  const dataRows = opts.rows.map(function (row) {
    return opts.columns.map(function (c) {
      const v = row[c.key];
      return escapeCsv(v == null ? '' : String(v));
    }).join(',');
  });
  const lines: string[] = [];
  if (opts.companyName) lines.push(escapeCsv(opts.companyName));
  if (opts.subtitle) lines.push(escapeCsv(opts.subtitle));
  if (opts.companyName || opts.subtitle) lines.push('');
  lines.push(headers);
  dataRows.forEach(function (r) { lines.push(r); });
  const csv = lines.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, fileName + '.csv');
}

function exportXlsx(fileName: string, opts: ExportOptions): void {
  const aoa: Array<Array<string | number | null | undefined>> = [];
  if (opts.companyName) aoa.push([opts.companyName]);
  if (opts.subtitle) aoa.push([opts.subtitle]);
  if (opts.companyName || opts.subtitle) aoa.push([]);
  aoa.push(opts.columns.map(function (c) { return c.label; }));
  opts.rows.forEach(function (row) {
    aoa.push(opts.columns.map(function (c) { return row[c.key] ?? ''; }));
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  // Apply column widths if provided
  ws['!cols'] = opts.columns.map(function (c) { return { wch: c.width ?? 20 }; });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, opts.reportName.slice(0, 31));
  XLSX.writeFile(wb, fileName + '.xlsx');
}

function exportPdf(fileName: string, opts: ExportOptions): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();

  let cursorY = 50;
  if (opts.companyName) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(opts.companyName, pageWidth / 2, cursorY, { align: 'center' });
    cursorY += 18;
  }
  if (opts.subtitle) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(opts.subtitle, pageWidth / 2, cursorY, { align: 'center' });
    cursorY += 20;
  }

  autoTable(doc, {
    startY: cursorY,
    head: [opts.columns.map(function (c) { return c.label; })],
    body: opts.rows.map(function (row) {
      return opts.columns.map(function (c) {
        const v = row[c.key];
        return v == null ? '' : String(v);
      });
    }),
    styles: { fontSize: 9, cellPadding: 5 },
    headStyles: { fillColor: [14, 26, 26], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [247, 248, 250] },
    margin: { left: 40, right: 40 },
  });

  doc.save(fileName + '.pdf');
}

// Helpers
function escapeCsv(value: string): string {
  if (value == null) return '';
  const needsEscape = /[",\n]/.test(value);
  if (!needsEscape) return value;
  return '"' + value.replace(/"/g, '""') + '"';
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}